import type { AdminCrewReportReportedVesselsGaugeProps } from "../../models/admin-crew-report.models";
import {
  formatAdminCrewReportReportingPercentage,
  getAdminCrewReportReportingPercentage,
} from "../../utils/admin-crew-report-statistics.utils";
import {
  ADMIN_CREW_REPORT_REPORTED_VESSELS_GAUGE_PATH,
  ADMIN_CREW_REPORT_REPORTING_CARD_CLASS_NAME,
  ADMIN_CREW_REPORT_REPORTING_COLORS,
  ADMIN_CREW_REPORT_STATISTICS_TITLE_CLASS_NAME,
} from "../../utils/admin-crew-report.constants";
import type { CSSProperties } from "react";

const AdminCrewReportReportedVesselsGauge = ({ presentation }: AdminCrewReportReportedVesselsGaugeProps) => {
  const primaryPercentage = getAdminCrewReportReportingPercentage(presentation.primaryPercentage);
  const reportedColorStyle = {
    "--admin-crew-report-reporting-color": ADMIN_CREW_REPORT_REPORTING_COLORS.reported.color,
    "--admin-crew-report-reporting-dark-color": ADMIN_CREW_REPORT_REPORTING_COLORS.reported.darkColor,
  } as CSSProperties;
  const notReportedColorStyle = {
    "--admin-crew-report-reporting-color": ADMIN_CREW_REPORT_REPORTING_COLORS.notReported.color,
    "--admin-crew-report-reporting-dark-color": ADMIN_CREW_REPORT_REPORTING_COLORS.notReported.darkColor,
  } as CSSProperties;

  return (
    <article
      data-test="admin-crew-report-reported-vessels-card"
      data-testid="admin-crew-report-reported-vessels-card"
      className={ADMIN_CREW_REPORT_REPORTING_CARD_CLASS_NAME}
    >
      <h3 className={ADMIN_CREW_REPORT_STATISTICS_TITLE_CLASS_NAME}>{presentation.gaugeTitle}</h3>
      {presentation.totalRunningVesselCount === 0 ? (
        <p className="flex flex-1 items-center justify-center px-2 text-center text-xs text-[#65706F] dark:text-gray-400">
          No running vessels are available.
        </p>
      ) : (
        <div
          role="img"
          aria-label={presentation.accessibleSummary}
          className="mt-3 flex min-h-0 flex-1 flex-col items-center justify-center"
        >
          <div className="relative w-full max-w-[15rem]" data-testid="admin-crew-report-reported-vessels-gauge">
            <svg className="block w-full" viewBox="0 0 200 108" aria-hidden="true">
              <path
                d={ADMIN_CREW_REPORT_REPORTED_VESSELS_GAUGE_PATH}
                fill="none"
                className="stroke-[var(--admin-crew-report-reporting-color)] dark:stroke-[var(--admin-crew-report-reporting-dark-color)]"
                style={notReportedColorStyle}
                strokeWidth="22"
              />
              {primaryPercentage > 0 && (
                <path
                  d={ADMIN_CREW_REPORT_REPORTED_VESSELS_GAUGE_PATH}
                  fill="none"
                  pathLength="100"
                  className="stroke-[var(--admin-crew-report-reporting-color)] dark:stroke-[var(--admin-crew-report-reporting-dark-color)]"
                  style={reportedColorStyle}
                  strokeDasharray={`${primaryPercentage} 100`}
                  strokeWidth="22"
                  data-testid="admin-crew-report-reported-vessels-gauge-reported"
                />
              )}
            </svg>
            <p
              aria-hidden="true"
              className="absolute inset-x-0 bottom-1 text-center text-3xl font-semibold tabular-nums leading-none text-[#24302E] dark:text-gray-100"
              data-testid="admin-crew-report-reported-vessels-count"
            >
              {presentation.primaryVesselCount}
            </p>
          </div>
          <p aria-hidden="true" className="mt-2 text-center text-[11px] leading-3 text-[#65706F] dark:text-gray-400">
            of {presentation.totalRunningVesselCount} running vessels
          </p>
          <p
            aria-hidden="true"
            className="mt-1 text-center text-xs font-semibold tabular-nums text-[var(--admin-crew-report-reporting-color)] dark:text-[var(--admin-crew-report-reporting-dark-color)]"
            style={reportedColorStyle}
            data-testid="admin-crew-report-reported-vessels-percentage"
          >
            {formatAdminCrewReportReportingPercentage(presentation.primaryPercentage)}{" "}
            {presentation.primaryPercentageLabel}
          </p>
        </div>
      )}
    </article>
  );
};

export default AdminCrewReportReportedVesselsGauge;
